<template>
  <div
    class="pause-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="pause-modal-title"
  >
    <div class="pause-modal__backdrop" @click="close"></div>
    <div class="pause-modal__shell">
      <header class="pause-modal__header">
        <div class="min-w-0">
          <h2 id="pause-modal-title" class="pause-modal__title">
            {{ scheduling ? 'Schedule a pause' : 'Pause subscription' }}
          </h2>
          <p v-if="subtitle" class="pause-modal__subtitle">{{ subtitle }}</p>
        </div>
        <button
          type="button"
          class="pause-modal__close"
          aria-label="Close"
          :disabled="pausing"
          @click="close"
        >
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </header>

      <div class="pause-modal__body">
        <div v-if="overdueLocked" class="pause-modal__locked" role="note">
          <strong>This subscription has an overdue payment.</strong>
          Once it's paid you can pause online. Questions? Contact your coach.
        </div>
        <div v-else-if="locked" class="pause-modal__locked" role="note">
          <strong>You've already paused this subscription in the last 12 months.</strong>
          Subscriptions can be paused online once every 12 months. You can pause again after
          {{ lockedUntilDate }}.
        </div>

        <!-- Club policy. Long policies collapse to a short preview so the controls stay in reach;
             the member can expand it in place. -->
        <div v-if="showPolicy" class="pause-modal__policy">
          <p class="pause-modal__policy-heading">Pause policy</p>
          <div
            ref="policyBody"
            class="pause-modal__policy-body trix-content"
            :class="{ 'is-collapsed': policyCollapsed }"
            v-html="policyHtml"
          ></div>
          <button
            v-if="policyOverflows"
            type="button"
            class="pause-modal__policy-toggle"
            :aria-expanded="policyExpanded ? 'true' : 'false'"
            @click="policyExpanded = !policyExpanded"
          >
            {{ policyExpanded ? 'Show less' : 'Read the full policy' }}
          </button>
        </div>

        <template v-if="!locked">
        <fieldset class="pause-modal__section">
          <legend class="pause-modal__label">When should the pause start?</legend>
          <div class="pause-modal__segments" role="radiogroup">
            <label class="pause-modal__segment" :class="{ 'is-selected': pauseStart === 'now' }">
              <input type="radio" value="now" id="pause-start-now" v-model="pauseStart" />
              <span>Right away</span>
            </label>
            <label class="pause-modal__segment" :class="{ 'is-selected': pauseStart === 'future' }">
              <input type="radio" value="future" id="pause-start-future" v-model="pauseStart" />
              <span>On a future date</span>
            </label>
          </div>
          <div v-if="scheduling" class="pause-modal__date">
            <date-picker
              v-model="pauseAt"
              :date="pauseAt"
              :disabled-date="tomorrowOrLater"
              placeholder="Pick the day payments pause"
            ></date-picker>
          </div>
        </fieldset>

        <fieldset class="pause-modal__section">
          <legend class="pause-modal__label">How long?</legend>
          <p v-if="needsStartDate" class="pause-modal__hint">
            Pick a start date to see which payments each option skips.
          </p>
          <div class="pause-modal__options" :class="{ 'is-loading': loadingOptions }">
            <label
              v-for="option in options"
              :key="option.intervals"
              class="pause-modal__option"
              :class="{ 'is-selected': pauseFor === String(option.intervals) }"
            >
              <input
                type="radio"
                :value="String(option.intervals)"
                :id="`pause-${option.intervals}`"
                v-model="pauseFor"
              />
              <span class="pause-modal__option-body">
                <span class="pause-modal__option-title">
                  {{ option.length }}
                  <span class="pause-modal__option-meta">{{ periodsLabel(option) }}</span>
                </span>
                <span v-if="!needsStartDate" class="pause-modal__option-detail">
                  {{ optionDetail(option) }}
                </span>
              </span>
            </label>
            <template v-if="allowPause">
              <label class="pause-modal__option" :class="{ 'is-selected': pauseFor === 'specific' }">
                <input type="radio" value="specific" id="specific-pause" v-model="pauseFor" />
                <span class="pause-modal__option-body">
                  <span class="pause-modal__option-title">Until a specific date</span>
                  <span v-if="pauseFor === 'specific'" class="pause-modal__date">
                    <date-picker
                      v-model="resumeAt"
                      :date="resumeAt"
                      :disabled-date="tomorrowOrLater"
                      placeholder="Pick the day payments resume"
                    ></date-picker>
                  </span>
                </span>
              </label>
              <label class="pause-modal__option" :class="{ 'is-selected': pauseFor === 'indefinite' }">
                <input type="radio" value="indefinite" id="indefinite-pause" v-model="pauseFor" />
                <span class="pause-modal__option-body">
                  <span class="pause-modal__option-title">
                    Indefinitely
                    <span class="pause-modal__option-meta">no auto-resume</span>
                  </span>
                  <span v-if="pauseFor === 'indefinite'" class="pause-modal__caution" role="note">
                    <strong>Are you sure?</strong> If you don't know when this wrestler is coming
                    back, canceling the subscription is the better option. An indefinite pause keeps
                    them on your books with no plan to bill again, and it's easy to forget about.
                  </span>
                </span>
              </label>
            </template>
          </div>
        </fieldset>

        <p v-if="memberOnly" class="pause-modal__note">
          Payments resume automatically when the pause ends. Time you've already paid for
          isn't refunded.
          <template v-if="oncePerYear">Subscriptions can be paused online once every 12 months.</template>
        </p>
        <p v-if="error" class="text-danger m-b-none">{{ errorMessage }}</p>
        </template>
      </div>

      <footer class="pause-modal__footer">
        <button type="button" class="btn btn-default" :disabled="pausing" @click="close">
          {{ locked ? 'Close' : 'Cancel' }}
        </button>
        <ladda-button
          v-if="!locked"
          @lbClicked="pausePayments"
          el-class="btn btn-warning"
          :loading="pausing"
        >
          {{ scheduling ? 'Schedule pause' : 'Pause subscription' }}
        </ladda-button>
      </footer>
    </div>
  </div>
</template>

<script>
import DatePicker from './date_picker.vue';
import LaddaButton from '../../shared/ladda_button.vue';
import { errorableMix } from './mixins/errorable_mix';
import { POLICY_COLLAPSED_HEIGHT, policyOverflows } from '../pause_policy';

// Pause payment collection on a subscription. Members (allowSelfPause) pick a whole number of
// billing periods, starting now or on a future date; coaches (allowPause) additionally get a
// specific resume date or an indefinite pause. The server resolves the resume instant for
// period-based pauses, so the option details are refetched whenever the start date changes.
export default {
  name: 'pause-payments-modal',
  components: { DatePicker, LaddaButton },
  mixins: [errorableMix],
  props: {
    subscription: {
      type: Object,
      required: true,
    },
    subtitle: {
      type: String,
      default: '',
    },
    // Coach: arbitrary resume date / indefinite pause, on top of the period presets.
    allowPause: {
      type: Boolean,
      default: false,
    },
    // Member self-pause: period presets only.
    allowSelfPause: {
      type: Boolean,
      default: false,
    },
    // [{ intervals, length, resumes_at }] resolved from now
    // (BillingSubscription#self_pause_options). Replaced when a future start date is picked.
    pauseOptions: {
      type: Array,
      default() {
        return [];
      },
    },
    // Club-authored pause rules (sanitized Trix HTML from Team#self_pause_policy_text), shown to
    // members above the choices. Coaches pausing on a member's behalf don't need it.
    policyHtml: {
      type: String,
      default: '',
    },
    // Team#self_pause_once_per_year: members get one self pause per rolling 12 months. Only
    // affects the copy here; the lock itself arrives as selfPauseLockedUntil.
    oncePerYear: {
      type: Boolean,
      default: true,
    },
    // ISO instant when a member can self-pause again, or null. Set when they've used their one
    // pause in the rolling 12 month window (BillingSubscription#self_pause_available_again_at);
    // the modal then explains instead of offering lengths. Never set for coaches.
    selfPauseLockedUntil: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      options: this.pauseOptions,
      loadingOptions: false,
      pauseStart: 'now',
      pauseAt: null,
      resumeAt: null,
      // Coaches default to a specific resume date: an indefinite pause is available but discouraged
      // (cancel instead when the return date is unknown). Members must pick a finite number of
      // billing periods, so default them to the shortest option.
      pauseFor: this.allowPause
        ? 'specific'
        : String(this.pauseOptions.length ? this.pauseOptions[0].intervals : 1),
      pausing: false,
      // Long policies start collapsed (see .pause-modal__policy-body.is-collapsed)
      policyOverflows: false,
      policyExpanded: false,
    };
  },
  computed: {
    scheduling() {
      return this.pauseStart === 'future';
    },
    needsStartDate() {
      return this.scheduling && !this.pauseAt;
    },
    memberOnly() {
      return this.allowSelfPause && !this.allowPause;
    },
    // Members can't self-pause an overdue subscription (the API refuses it); a coach still can.
    overdueLocked() {
      return this.memberOnly && this.subscription.status === 'overdue';
    },
    locked() {
      return this.overdueLocked || (this.memberOnly && !!this.selfPauseLockedUntil);
    },
    showPolicy() {
      return !!this.policyHtml && (this.memberOnly || this.locked);
    },
    policyCollapsed() {
      return this.policyOverflows && !this.policyExpanded;
    },
    lockedUntilDate() {
      return this.selfPauseLockedUntil ? moment(this.selfPauseLockedUntil).format('MMMM D, YYYY') : '';
    },
  },
  watch: {
    pauseStart(start) {
      if (start === 'now') {
        this.options = this.pauseOptions;
      } else if (this.pauseAt) {
        this.fetchOptions(this.pauseAt);
      }
    },
    pauseAt(date) {
      if (this.scheduling && date) {
        this.fetchOptions(date);
      }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    // Measured before any collapse applies, so scrollHeight is the policy's natural height
    this.$nextTick(() => {
      this.policyOverflows = policyOverflows(this.$refs.policyBody, POLICY_COLLAPSED_HEIGHT);
    });
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    close() {
      if (this.pausing) return;
      this.$emit('close');
    },
    onKeydown(event) {
      if (event.key === 'Escape') this.close();
    },
    // "1 billing period", "2 billing periods"
    periodsLabel(option) {
      return option.intervals === 1
        ? '1 billing period'
        : `${option.intervals} billing periods`;
    },
    // "Resumes 10/2/2026." — the concrete date billing picks back up.
    optionDetail(option) {
      return `Resumes ${moment(option.resumes_at).format('M/D/YYYY')}.`;
    },
    // Func to disable past dates in the date-picker
    tomorrowOrLater(date) {
      const tomorrow = new Date();
      tomorrow.setHours(0, 0, 0, 0);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return date < tomorrow;
    },
    fetchOptions(startDate) {
      const vm = this;
      vm.loadingOptions = true;
      const url = vm.$apiService.subscriptionPauseOptionsUrl(vm.subscription.id);
      axios
        .get(url, { params: { from: moment(startDate).format('YYYY-MM-DD') } })
        .then((response) => {
          vm.options = response.data.pause_options || [];
          vm.loadingOptions = false;
        })
        .catch(() => {
          // Keep the last known options; the labels still read correctly without the dates.
          vm.loadingOptions = false;
        });
    },
    pausePayments: _.throttle(function () {
      const vm = this;
      vm.error = false;

      if (vm.scheduling && !vm.pauseAt) {
        vm.errorMessage = 'Please pick the day the pause should start, or switch to pausing right away.';
        vm.error = true;
        return;
      }

      // Resolve the selected option:
      //  - 'indefinite' (coach only) -> no resume date
      //  - 'specific' (coach only)   -> the chosen date-picker value
      //  - a number                  -> that many whole billing periods (member self-pause and coach
      //                                 presets); the server steps the bill grid to find the resume date
      let resumesAt = null;
      let intervals = null;
      if (vm.pauseFor === 'specific') {
        if (!vm.resumeAt) {
          vm.errorMessage = 'Please pick the day payments should resume, '
            + 'or choose a number of billing periods.';
          vm.error = true;
          return;
        }
        resumesAt = vm.resumeAt.toISOString();
      } else if (vm.pauseFor !== 'indefinite') {
        intervals = parseInt(vm.pauseFor, 10);
        if (Number.isNaN(intervals)) {
          vm.errorMessage = 'Please choose how long to pause subscription.';
          vm.error = true;
          return;
        }
      }
      if (vm.scheduling && vm.pauseFor === 'specific' && vm.resumeAt <= vm.pauseAt) {
        vm.errorMessage = 'The resume date needs to be after the day the pause starts.';
        vm.error = true;
        return;
      }

      vm.pausing = true;

      const pauseUrl = vm.$apiService.subscriptionPausePaymentsUrl(vm.subscription.id);
      const pauseParams = {
        billing_subscription: {
          pause_collection: {
            behavior: 'void',
            resumes_at: resumesAt,
            intervals,
            pauses_at: vm.scheduling && vm.pauseAt ? vm.pauseAt.toISOString() : null,
          },
        },
      };
      axios
        .post(pauseUrl, pauseParams)
        .then(() => {
          vm.$notificationManager.emit(
            'show-toast',
            vm.scheduling ? 'Payment pause scheduled' : 'Payments paused',
            true,
          );
          vm.pausing = false;
          window.location.reload();
        })
        .catch((error) => {
          // Surface the server-side self-pause guard message (e.g. an unavailable pause length)
          // when present; otherwise fall back to a generic message.
          const serverErrors = error?.response?.data?.errors;
          const serverMessage = Array.isArray(serverErrors)
            ? serverErrors.filter((e) => typeof e === 'string').pop()
            : null;
          vm.errorMessage = serverMessage
            || 'Something went wrong pausing payments. Try again or email support@wrestlingiq.com';
          vm.error = true;
          vm.pausing = false;
        });
    }, 500),
  },
};
</script>
